<template>
  <b-modal
    id="service-add"
    :title="$t('maintenance.order.addService')"
    size="md"
    centered
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    no-close-on-route-change
    no-caret
  >
    <McAlert
      :text="$t('maintenance.order.text.serviceAdd')"
      variant="info"
      withIcon
      class="d-flex justify-content-start mt-3"
    />

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-between">
        <div>
          <McButton
            variant="secondary"
            class="me-2"
            icon="icon-fill_close"
            :text="$t('global.action.cancel')"
            @click="cancelAddService()"
          />
        </div>
        <div class="d-flex flex-row">
          <McButtonValueHelp
            icon="icon-outline_cube"
            :text="$t('maintenance.order.otherService')"
            variant="secondary"
            :label="`${$t('global.addField')} ${$t('global.object.from')} ${$t(
              'maintenance.order.otherService'
            )}`"
            :multiple="false"
            :trackBy="'serviceId'"
            :listGetter="resourcefulQuery.otherService"
            :tableFields="otherTableFields"
            class="me-2"
            @selected="($event) => addData($event, 'Insidental')"
          />
          <McButtonValueHelp
            variant="primary"
            :label="`${$t('global.addField')} ${$t('global.object.from')} ${$t(
              'maintenance.order.serviceType.Schedule'
            )}`"
            :multiple="false"
            :trackBy="'serviceId'"
            icon="icon-outline_calendar"
            :listGetter="resourcefulQuery.serviceSchedule"
            :tableFields="scheduleTableFields"
            :text="$t('maintenance.order.serviceScheduled')"
            @selected="($event) => addData($event, 'Schedule')"
          />
        </div>
      </div>
    </template>
  </b-modal>
</template>
<script>
import McButton from '@/components/McButton'
import { GET_SERVICE_ORDER } from '@/services/api/maintenance/serviceorder'
import { SERVICES } from '@/services/api/setting/maintenance/setting/services'
import { GET_SERVICE_SCHEDULES } from '@/services/api/maintenance/serviceSchedules'
import McButtonValueHelp from '@/components/McButtonValueHelp'
import McAlert from '@/components/McAlert'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McButtonValueHelp,
    McAlert,
  },

  mixins: [Helper],

  apollo: {
    serviceOrder: {
      query: GET_SERVICE_ORDER,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            serviceOrderId: +this.$route.params.id,
          },
        }
      },
      result({ data, loading }) {
        if (loading) return

        this.serviceOrder = data.serviceOrder.serviceOrder
        this.vehicleId = this.serviceOrder.vehicleId

        this.render = true
      },
    },
  },

  props: ['serviceIds'],

  data() {
    return {
      render: false,
      serviceOrder: [],
      vehicleId: null,
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    isMGMAndMO() {
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(this.getCompanyId()) &&
        data.features?.maintenance_order
      )
    },
    otherTableFields() {
      return [
        {
          key: 'name',
          label: this.$t('maintenance.field.compName'),
          formatter: (val, key, row) => {
            return `${val} ${row.serviceAction?.action ? `(${row.serviceAction?.action})` : ''}`
          },
          sortable: true,
        },
        {
          key: 'serviceCategory.name',
          label: this.$t('global.field.category'),
          sortable: true,
        },
      ]
    },

    scheduleTableFields() {
      return [
        {
          key: 'service.name',
          label: this.$t('maintenance.field.compName'),
          formatter: (val, key, row) => {
            return `${val} ${
              row.service?.serviceAction?.action ? `(${row.service?.serviceAction?.action})` : ''
            }`
          },
          sortable: true,
        },
        {
          key: 'formatStatus',
          label: this.$t('maintenance.field.statusService'),
          sortable: true,
          hasBadge: true,
        },
        {
          key: 'ssgroup.name',
          label: this.$t('maintenance.frontPage.title.serviceGroup'),
          sortable: true,
          formatter: (val) => {
            return val ? val : '-'
          },
        },
        {
          key: 'service.serviceCategory',
          label: this.$t('maintenance.form.catService'),
          sortable: true,
          formatter: (val) => {
            return val ? val.name : '-'
          },
        },
        {
          key: 'differences',
          label: this.$t('dashboard.text.difference'),
          sortable: true,
        },
      ]
    },

    resourcefulQuery() {
      return {
        otherService: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: SERVICES,
            variables: {
              filter: {
                ...filter,
                excludeServiceId: this.serviceIds,
              },
            },
          })

          const result = {
            metadata: {
              total_count: res.data.services.services.pagination.total,
              page: res.data.services.services.pagination.page,
            },
            data: {
              paginated_result: res.data.services.services.data,
            },
          }

          return result
        },
        serviceSchedule: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_SERVICE_SCHEDULES,
            variables: {
              filter: {
                ...filter,
                vehicleId: this.vehicleId,
                vehicleStatus: ['I', 'R'],
                excludeServiceId: this.serviceIds,
                type: [
                  'DistanceAndTime',
                  'Hourmeter',
                  ...(this.isMGMAndMO ? ['Refrigerator'] : []),
                ],
                status: [
                  'lateOdometer',
                  'lateHourmeter',
                  ...(this.isMGMAndMO ? ['lateRefrigerator'] : []),
                  'lateDate',
                  'soonOdometer',
                  'soonHourmeter',
                  ...(this.isMGMAndMO ? ['soonRefrigerator'] : []),
                  'soonDate',
                  'normalOdometer',
                  'normalHourmeter',
                  ...(this.isMGMAndMO ? ['normalRefrigerator'] : []),
                  'normalDate',
                ],
              },
            },
          })

          const { serviceSchedules } = res.data.serviceSchedules.serviceSchedules.data

          serviceSchedules.forEach((row) => {
            let format = { color: 'success', label: this.$t('maintenance.status.normal') }
            if (row.status.includes('I')) {
              format = { color: 'inprogress', label: this.$t('maintenance.status.inService') }
            } else if (
              row.status.includes('LO') ||
              row.status.includes('LD') ||
              row.status.includes('LH')
            ) {
              format = { color: 'canceled', label: this.$t('maintenance.status.lateService') }
            } else if (
              row.status.includes('SO') ||
              row.status.includes('SD') ||
              row.status.includes('SH')
            ) {
              format = {
                color: 'confirmOrder',
                label: this.$t('maintenance.status.approachingService'),
              }
            }

            row.formatStatus = format

            const time = row.serviceTimeDifference
            const timeDifference = `${time.isDatePassed ? '-' : ''}${
              time.months == 0 && time.days == 0
                ? `0 ${this.$t('global.time.days')}`
                : `${time.months ? time.months + ` ${this.$t('global.time.month')} ` : ''}${
                    time.days ? time.days + ` ${this.$t('global.time.days')}` : ''
                  }`
            }`

            const kilometerDifference = row.serviceDistanceDifference + ' Km & ' + timeDifference
            const hourmeterDifference = this.minuteToHour(row.serviceHourmeterDifference)

            row.differences = row.type === 'Hourmeter' ? hourmeterDifference : kilometerDifference
          })

          const result = {
            metadata: {
              total_count: res.data.serviceSchedules.serviceSchedules.pagination.total,
              page: res.data.serviceSchedules.serviceSchedules.pagination.page,
            },
            data: {
              paginated_result: serviceSchedules,
            },
          }

          return result
        },
      }
    },
  },

  methods: {
    cancelAddService() {
      this.$bvModal.hide('service-add')
    },

    addData(selected, serviceType) {
      this.$emit('addData', selected.serviceId, serviceType, selected.type)
    },
  },
}
</script>
